import Button from "~/components/ui/Button"
import { invoke } from "~/runtime"
import clsx from "~/sdk/clx"
import { useSignal } from "~/sdk/signal"
import type { JSX } from "react"

/**
 * @titleBy placeholder
 */
export interface Input {
    placeholder: string
    /**
     * @title Obrigatório
     * @description Se o campo é obrigatório ou não
     * @default false
     */
    required: boolean
    /**
     * @title Tipo
     */
    type: 'Nome' | 'Email'
}

export interface CTA {
    /**
     * @title Texto CTA
     * @description Texto do botão de "enviar"
     */
    text: string
}

interface Props {
    inputs: Input[]
    blackBg?: boolean
    cta: CTA
    feedback: string
}

const INPUT_TYPE_MAPPINGS = {
    Nome: 'text',
    Email: 'email',
}

const INPUT_NAME_MAPPINGS = {
    Nome: 'name',
    Email: 'email',
}

type InputType = keyof typeof INPUT_TYPE_MAPPINGS

export default function Form({ inputs, blackBg, cta, feedback }: Props) {
    const loading = useSignal(false)
    const createdAccount = useSignal(false)

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        loading.value = true

        try {
            const formData = new FormData(e.currentTarget)
            const data = {
                nome: formData.get('name') as string,
                email: formData.get('email') as string,
            }

            await invoke.site.actions.newsletter({ data })

            createdAccount.value = true
        } finally {
            loading.value = false
        }
    }

    if (createdAccount.value) {
        return (
            <div
                className='bg-white border-4 border-salmon text-black p-1 font-semibold [&_strong]:font-bolder'
                dangerouslySetInnerHTML={{ __html: feedback }}
            />
        )
    }

    return (
        <form
            onSubmit={handleSubmit}
            autoComplete='off'
            className='flex gap-2 lg:gap-5 justify-center items-center max-w-[31.875rem] mx-auto w-full'
        >
            {inputs.map((props, index) => (
                <input
                    key={`${index}-${props.type}`}
                    {...props}
                    disabled={loading.value}
                    name={INPUT_NAME_MAPPINGS[props.type as InputType] ?? 'name'}
                    type={INPUT_TYPE_MAPPINGS[props.type as InputType] ?? 'text'}
                    aria-label={props.placeholder}
                    className={clsx(
                        'input input-ghost lg:placeholder:text-neutral-700 bg-transparent min-w-0 rounded-none border-0 border-b-[0.0625rem] border-[#d7d7d7] focus:border-white lg:border-white px-2 text-xs h-[38px] lg:text-sm disabled:text-black',
                        blackBg
                            ? 'text-white focus:text-white focus-within:text-white'
                            : 'text-black focus:text-black focus-within:text-black',
                    )}
                />
            ))}
            <Button
                disabled={loading.value}
                loading={loading.value}
                className={`h-10 w-24 lg:h-8 lg:w-20 font-bold text-white bg-salmon border-none focus:outline-none hover:bg-salmon-600 min-h-8 rounded-none disabled:bg-salmon-light`}
                type='submit'
            >
                {cta.text}
            </Button>
        </form>
    )
}
